import { useLoaderData } from 'react-router-dom'
import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import { styled } from '@mui/material/styles'
import DownloadIcon from '@mui/icons-material/Download'
import { useTranslation } from 'react-i18next'
import { GridColDef, GridInitialState } from '@mui/x-data-grid-premium'
import upperFirst from 'lodash/upperFirst'

import { LoaderData, Certificates } from '../../types'
import { formatHumanDate } from '../../utils'
import { certificatesLoader } from './ViewCertificates.loader'
import StripedDataGrid from '../root/components/StripedDataGrid'
import BreadCrumbs from '../root/components/BreadCrumbs'
import SubHeader from '../root/components/SubHeader'
import { HEADER_HEIGHT } from '../root/components/Header'
import { useLazyGetCertificateQuery } from '../../services/events'

const Background = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.colors.secondaryBackground,
  height: `calc(100% - ${HEADER_HEIGHT} - 1px)`,
  width: '100%',
}))

const initialState: GridInitialState = {
  columns: {
    columnVisibilityModel: {
      id: false,
      event_date: false,
    },
  },
  sorting: {
    sortModel: [
      {
        field: 'event_date',
        sort: 'desc',
      },
    ],
  },
}

export default function ViewCertificates() {
  const { t } = useTranslation('dashboard')

  const data = useLoaderData() as LoaderData<ReturnType<typeof certificatesLoader>>
  const [getCertificate] = useLazyGetCertificateQuery()

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'actions',
        headerName: t('Actions'),
        type: 'actions',
        width: 100,
        filterable: false,
        disableExport: true,
        renderCell: params => {
          const row = params.row as Certificates
          return (
            <Stack direction="row">
              <Tooltip title={t("Télécharger l'attestation")}>
                <IconButton
                  onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    void getCertificate({ eventId: row.event_id, participantId: row.id })
                  }}
                  color="primary"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )
        },
      },
      {
        field: 'event_date',
        headerName: t('Date'),
        width: 250,
        type: 'dateTime',
        valueGetter: value => value && new Date(value),
        valueFormatter: (value: Date) =>
          value ? upperFirst(formatHumanDate(value.toISOString())) : 'NC',
      },
      {
        field: 'event_name',
        headerName: t('Nom'),
        width: 500,
      },
    ],
    [getCertificate, t]
  )

  return (
    <Background>
      <SubHeader>
        <BreadCrumbs
          hasSeparatorEnd
          links={[{ name: t('Mes attestations'), pathname: '' }]}
          sx={{ flex: '1 0' }}
        />
      </SubHeader>
      <Stack
        sx={theme => ({
          background: theme.palette.background.default,
          width: '100%',
          height: '100%',
          padding: '1rem',
          overflowY: 'auto',
        })}
        gap="1rem"
      >
        <Stack
          sx={theme => ({
            backgroundColor: theme.palette.background.paper,
            borderRadius: '0.5rem',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.boxShadows.main,
          })}
          padding="1rem"
          height="inherit"
          overflow="auto"
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <h3>{t('Liste des attestations de présence')} :</h3>
          </Stack>

          <Stack
            direction={{ md: 'column', lg: 'row' }}
            alignItems={{ md: 'left', lg: 'center' }}
            justifyContent={'space-between'}
            marginBottom="1rem"
          >
            {t(
              'Pour consulter vos attestions de présence antérieures à Septembre 2024, veuillez vous rendre sur votre profil SFCTCV'
            )}{' '}
            :
            <Button href="https://www.sfctcv.org/mes-attestations-de-presence/" target="_blanck">
              {t('Attestions de présence antérieures')}
            </Button>
          </Stack>

          <Stack flexGrow="1">
            <StripedDataGrid
              rows={data}
              columns={columns}
              count={data?.length}
              paginationMode="client"
              initialState={initialState}
              hideExport
              checkboxSelection={false}
            />
          </Stack>
        </Stack>
      </Stack>
    </Background>
  )
}
